import { IconAppWindow, IconArrowRight, IconArticle, IconSchool, type TablerIcon } from "@tabler/icons-react"
import { type FC } from "react"

import { aiGenerationCreditCosts } from "@forento/core/models/ai"
import { toNumberFormat } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import AiCreditBalanceWarning from "~/components/AiCreditBalanceWarning"
import Link from "~/components/Link"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"

type QuickAction = { label: string; Icon: TablerIcon; link: string; cost: number }

const ForentoAiCard: FC = () => {
	const platform = usePlatform()!

	const canUseWebsite = platform.plan.access.landingPage
	const quickActions: QuickAction[] = [
		{
			label: "Generate a course",
			Icon: IconSchool,
			link: `${routes.course.index()}?create=ai`,
			cost: aiGenerationCreditCosts.course,
		},
		...(canUseWebsite && platform.landingPage?.type === "landing-page"
			? [
					{
						label: "Write a blog post",
						Icon: IconArticle,
						link: `${routes.main.websiteBlog()}?ai=true`,
						cost: aiGenerationCreditCosts["blog-post"],
					},
				]
			: []),
		...(canUseWebsite &&
		(platform.landingPage?.type === "landing-page" || platform.landingPage?.type === "migrating")
			? [
					{
						label: "Add a website page",
						Icon: IconAppWindow,
						link: `${routes.main.websiteEditor()}?ai=true`,
						cost: aiGenerationCreditCosts["landing-page-additional-page"],
					},
				]
			: canUseWebsite && platform.landingPage === null
				? [
						{
							label: "Generate your website",
							Icon: IconAppWindow,
							link: `${routes.main.websiteEditor()}?ai=true`,
							cost: aiGenerationCreditCosts["landing-page"],
						},
					]
				: []),
	]

	return (
		<section className="flex flex-col gap-4 rounded-3xl bg-[#f9f5ff] p-3 sm:p-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="text-primary text-sm font-semibold">Forento AI</p>
					<h2 className="text-xl font-semibold text-black">Create with AI</h2>
					<p className="mt-1 text-sm leading-5 text-gray-600">
						You have{" "}
						<span className="font-semibold text-black tabular-nums">
							{toNumberFormat(platform.aiCreditBalance)}
						</span>{" "}
						credits left.
					</p>
				</div>
				<Link
					to={routes.settings.billing.plans()}
					className="text-primary flex shrink-0 items-center gap-1 text-sm font-semibold"
				>
					Buy more credits
					<IconArrowRight className="size-4" aria-hidden />
				</Link>
			</div>
			<AiCreditBalanceWarning />
			<div className="flex flex-wrap gap-2">
				{quickActions.map(action => (
					<Link
						key={action.label}
						to={action.link}
						className="group hover:text-primary flex min-w-0 flex-[1_1_190px] items-center gap-3 rounded-2xl bg-white/70 p-3 text-black transition-colors hover:bg-white"
					>
						<span className="text-primary flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#f9f5ff]">
							<action.Icon className="size-5" aria-hidden />
						</span>
						<span className="min-w-0 flex-1">
							<span className="block text-sm leading-5 font-semibold">{action.label}</span>
							<span
								className={cn(
									"block text-xs font-medium tabular-nums",
									platform.aiCreditBalance >= action.cost ? "text-gray-500" : "text-red-600",
								)}
							>
								{toNumberFormat(action.cost)} credits
							</span>
						</span>
						<IconArrowRight
							className="text-primary size-4 shrink-0 transition-transform group-hover:translate-x-0.5"
							aria-hidden
						/>
					</Link>
				))}
			</div>
		</section>
	)
}

export default ForentoAiCard
